import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, Download, Loader2, AlertTriangle, FileText, ImageIcon, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function AutorizarTermo() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [usoImagem, setUsoImagem] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Link inválido: token ausente.");
      return;
    }
    api.get(`/termo/${token}`)
      .then((r) => {
        setData(r.data);
        setSigned(r.data.status === "assinado");
      })
      .catch((err) => setError(formatApiError(err.response?.data?.detail)));
  }, [token]);

  const autorizar = async () => {
    if (usoImagem === null) {
      toast.error("Selecione se autoriza ou não o uso de imagem antes de aprovar o termo.");
      return;
    }
    setSigning(true);
    try {
      await api.post(`/termo/${token}/autorizar`, { uso_imagem: usoImagem });
      setSigned(true);
    } catch (err) {
      if (err.response?.status === 400) setSigned(true);
      else setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  const h = data?.sections?.header;

  return (
    <div className="min-h-screen bg-obsidian text-[#F5F5F0]" data-testid="autorizar-termo-page">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="relative max-w-2xl mx-auto px-5 py-10 sm:py-14">
        <header className="flex items-center gap-4 border-b border-gold/30 pb-6">
          <img src="/assets/logo.jpeg" alt="Projeto Bonsai BJJ" className="w-14 h-14 rounded-full object-cover ring-1 ring-gold/50" />
          <div>
            <p className="font-display font-bold text-lg">PROJETO <span className="text-gold">BONSAI</span> BJJ</p>
            <p className="font-mono text-[10px] tracking-widest text-zinc-500">TERMO DE RESPONSABILIDADE E CIÊNCIA DE RISCOS</p>
          </div>
        </header>

        {error ? (
          <div className="mt-10 flex flex-col items-center gap-3 border border-dashed border-crimson/40 rounded-2xl py-14 text-center" data-testid="termo-error">
            <AlertTriangle size={26} className="text-crimson" />
            <p className="text-sm text-zinc-400">{error}</p>
          </div>
        ) : !data ? (
          <p className="mt-10 text-sm text-zinc-500 flex items-center gap-2" data-testid="termo-loading">
            <Loader2 size={15} className="animate-spin" /> Carregando termo...
          </p>
        ) : (
          <div className="mt-8">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-2" data-testid="termo-identificacao">
              <p className="font-mono text-[10px] tracking-widest text-gold mb-2">IDENTIFICAÇÃO</p>
              <p className="text-sm"><span className="text-zinc-500">Aluno(a):</span> <strong data-testid="termo-aluno">{h.aluno}</strong> <span className="text-zinc-500">· Nascimento:</span> {h.nascimento}</p>
              <p className="text-sm"><span className="text-zinc-500">Responsável legal:</span> <strong data-testid="termo-responsavel">{h.responsavel}</strong> <span className="text-zinc-500">·</span> {h.parentesco}</p>
              <p className="text-sm"><span className="text-zinc-500">CPF:</span> {h.cpf} <span className="text-zinc-500">· RG:</span> {h.rg}</p>
              <p className="text-sm"><span className="text-zinc-500">E-mail:</span> {h.email} <span className="text-zinc-500">· Telefone:</span> {h.telefone}</p>
            </div>

            <div className="mt-6 space-y-4" data-testid="termo-texto">
              <p className="text-sm text-zinc-300 leading-relaxed">{data.sections.intro}</p>
              {data.sections.clauses.map(([title, body], i) => (
                <div key={i}>
                  <p className="font-display font-semibold text-sm text-gold">{title}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed mt-1">{body}</p>
                </div>
              ))}
            </div>

            {!signed && data.sections.imagem && (
              <div className="mt-6 rounded-2xl border border-gold/30 bg-gold/[0.04] p-5" data-testid="termo-imagem-step">
                <p className="font-display font-semibold text-sm text-gold">7. Uso de imagem</p>
                <p className="text-sm text-zinc-400 leading-relaxed mt-1">{data.sections.imagem.pergunta}</p>
                <div className="mt-4 grid gap-3">
                  <button type="button" onClick={() => setUsoImagem(true)} data-testid="termo-imagem-autorizo"
                    className={`text-left rounded-xl border p-4 transition-colors flex items-start gap-3 ${usoImagem === true ? "border-gold bg-gold/15 text-[#F5F5F0]" : "border-white/15 text-zinc-400 hover:border-gold/40"}`}>
                    <ImageIcon size={18} className={usoImagem === true ? "text-gold shrink-0 mt-0.5" : "shrink-0 mt-0.5"} />
                    <span className="text-sm leading-snug"><strong>AUTORIZO</strong> o uso de imagem — o(a) aluno(a) pode aparecer nas fotos e vídeos do projeto.</span>
                  </button>
                  <button type="button" onClick={() => setUsoImagem(false)} data-testid="termo-imagem-nao"
                    className={`text-left rounded-xl border p-4 transition-colors flex items-start gap-3 ${usoImagem === false ? "border-crimson bg-crimson/15 text-[#F5F5F0]" : "border-white/15 text-zinc-400 hover:border-crimson/40"}`}>
                    <ImageOff size={18} className={usoImagem === false ? "text-crimson shrink-0 mt-0.5" : "shrink-0 mt-0.5"} />
                    <span className="text-sm leading-snug"><strong>NÃO AUTORIZO</strong> — o rosto do(a) aluno(a) será censurado em qualquer foto ou vídeo divulgado.</span>
                  </button>
                </div>
              </div>
            )}

            {signed ? (
              <div className="mt-8 rounded-2xl border border-gold/40 bg-gold/[0.06] p-6 text-center" data-testid="termo-assinado">
                <ShieldCheck size={30} className="text-gold mx-auto" />
                <p className="mt-3 font-display font-bold">Termo assinado digitalmente</p>
                <p className="mt-1 text-sm text-zinc-400">
                  Uma cópia em PDF foi enviada para o e-mail do responsável e salva no cadastro do(a) aluno(a).
                  {usoImagem !== null && (
                    <> Uso de imagem: <strong className={usoImagem ? "text-gold" : "text-crimson"}>{usoImagem ? "AUTORIZADO" : "NÃO AUTORIZADO"}</strong> — o cadastro do(a) aluno(a) já foi ajustado.</>
                  )} Oss!
                </p>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/termo/${token}/pdf`} data-testid="termo-pdf-download"
                  className="mt-5 inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-7 py-3 rounded-full hover:bg-gold-amber transition-colors">
                  <Download size={16} /> Baixar PDF assinado
                </a>
              </div>
            ) : (
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center">
                <FileText size={26} className="text-gold mx-auto" />
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                  Ao clicar em <strong className="text-[#F5F5F0]">AUTORIZAR</strong>, o sistema registra data, hora, endereço IP e um código de autenticação vinculado ao seu e-mail, gerando o termo em PDF assinado digitalmente.
                </p>
                <button type="button" onClick={autorizar} disabled={signing} data-testid="termo-autorizar-button"
                  className="mt-5 inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-60">
                  {signing ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                  {signing ? "Registrando..." : "AUTORIZAR A PARTICIPAÇÃO"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
